// src/utils/countryUtils.ts

/**
 * Maps country codes (ISO 3166-1 alpha-2 + football-specific codes)
 * to a human-readable name.
 *
 * GB is kept as a fallback only — clubs should use ENG / SCO / WAL / NIR.
 * No flag emojis: regional indicator letters (e.g. 🇮🇹) render as the
 * letters "IT" in most browsers, causing duplicates like "IT IT".
 * The England subregion flag (🏴󠁧󠁢󠁥󠁮󠁧󠁿) renders as an actual image, inconsistent.
 */
const COUNTRY_MAP: Record<string, string> = {
  // UK nations — football uses its own codes, not ISO GB
  ENG: "England",
  SCO: "Scotland",
  WAL: "Wales",
  NIR: "N. Ireland",
  // GB fallback — shown when the club record hasn't been migrated yet
  GB:  "Great Britain",

  // Europe
  TR: "Türkiye",
  DE: "Germany",
  FR: "France",
  ES: "Spain",
  IT: "Italy",
  PT: "Portugal",
  NL: "Netherlands",
  BE: "Belgium",
  AT: "Austria",
  CH: "Switzerland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  PL: "Poland",
  CZ: "Czechia",
  SK: "Slovakia",
  HU: "Hungary",
  RO: "Romania",
  BG: "Bulgaria",
  HR: "Croatia",
  RS: "Serbia",
  BA: "Bosnia",
  SI: "Slovenia",
  ME: "Montenegro",
  MK: "N. Macedonia",
  AL: "Albania",
  GR: "Greece",
  CY: "Cyprus",
  IE: "Ireland",
  RU: "Russia",
  UA: "Ukraine",
  BY: "Belarus",
  GE: "Georgia",
  AM: "Armenia",
  AZ: "Azerbaijan",
  IS: "Iceland",
  LU: "Luxembourg",
  MT: "Malta",

  // Americas
  US: "USA",
  CA: "Canada",
  MX: "Mexico",
  BR: "Brazil",
  AR: "Argentina",
  CO: "Colombia",
  CL: "Chile",
  PE: "Peru",
  UY: "Uruguay",
  EC: "Ecuador",
  VE: "Venezuela",
  PY: "Paraguay",
  BO: "Bolivia",

  // Africa
  ZA: "South Africa",
  EG: "Egypt",
  MA: "Morocco",
  NG: "Nigeria",
  GH: "Ghana",
  SN: "Senegal",
  CM: "Cameroon",
  CI: "Côte d'Ivoire",
  TN: "Tunisia",
  DZ: "Algeria",

  // Asia & Middle East
  JP: "Japan",
  KR: "South Korea",
  CN: "China",
  IN: "India",
  SA: "Saudi Arabia",
  AE: "UAE",
  QA: "Qatar",
  IL: "Israel",
  IR: "Iran",
  IQ: "Iraq",
  AU: "Australia",
};

/**
 * Turkish display names for country codes.
 * Used wherever the UI language is Turkish (club/team page meta, etc.).
 * Falls back to COUNTRY_MAP (English) when a Turkish name is not defined.
 */
const COUNTRY_MAP_TR: Record<string, string> = {
  // UK nations
  ENG: "İngiltere",
  SCO: "İskoçya",
  WAL: "Galler",
  NIR: "Kuzey İrlanda",
  GB:  "Büyük Britanya",

  // Europe
  TR: "Türkiye",
  DE: "Almanya",
  FR: "Fransa",
  ES: "İspanya",
  IT: "İtalya",
  PT: "Portekiz",
  NL: "Hollanda",
  BE: "Belçika",
  AT: "Avusturya",
  CH: "İsviçre",
  SE: "İsveç",
  NO: "Norveç",
  DK: "Danimarka",
  FI: "Finlandiya",
  PL: "Polonya",
  CZ: "Çekya",
  SK: "Slovakya",
  HU: "Macaristan",
  RO: "Romanya",
  BG: "Bulgaristan",
  HR: "Hırvatistan",
  RS: "Sırbistan",
  BA: "Bosna Hersek",
  SI: "Slovenya",
  ME: "Karadağ",
  MK: "Kuzey Makedonya",
  AL: "Arnavutluk",
  GR: "Yunanistan",
  CY: "Kıbrıs",
  IE: "İrlanda",
  RU: "Rusya",
  UA: "Ukrayna",
  BY: "Belarus",
  GE: "Gürcistan",
  AM: "Ermenistan",
  AZ: "Azerbaycan",
  IS: "İzlanda",
  LU: "Lüksemburg",
  MT: "Malta",

  // Americas
  US: "ABD",
  CA: "Kanada",
  MX: "Meksika",
  BR: "Brezilya",
  AR: "Arjantin",
  CO: "Kolombiya",
  CL: "Şili",
  PE: "Peru",
  UY: "Uruguay",
  EC: "Ekvador",
  VE: "Venezuela",
  PY: "Paraguay",
  BO: "Bolivya",

  // Africa
  ZA: "Güney Afrika",
  EG: "Mısır",
  MA: "Fas",
  NG: "Nijerya",
  GH: "Gana",
  SN: "Senegal",
  CM: "Kamerun",
  CI: "Fildişi Sahili",
  TN: "Tunus",
  DZ: "Cezayir",

  // Asia & Middle East
  JP: "Japonya",
  KR: "Güney Kore",
  CN: "Çin",
  IN: "Hindistan",
  SA: "Suudi Arabistan",
  AE: "BAE",
  QA: "Katar",
  IL: "İsrail",
  IR: "İran",
  IQ: "Irak",
  AU: "Avustralya",
};

/**
 * Returns just the country code (e.g. "ENG", "TR", "DE").
 * No flag emojis — letter-based flags like 🇮🇹 render as "IT" in browsers,
 * causing duplicates like "IT IT" next to the code.
 */
export function getCountryLabel(code: string | null | undefined): string {
  if (!code) return "";
  return code.toUpperCase();
}

/**
 * Returns an empty string — flags removed to avoid browser rendering issues.
 * Kept for API compatibility in case other components reference it.
 */
export function getCountryFlag(code: string | null | undefined): string {
  return "";
}

/**
 * Returns the display name for a country code (e.g. "England", "Türkiye").
 * Falls back to the raw code if unknown.
 */
export function getCountryName(code: string | null | undefined): string {
  if (!code) return "";
  const upper = code.toUpperCase();
  return COUNTRY_MAP[upper] ?? upper;
}

/**
 * Returns the Turkish display name for a country code (e.g. "İskoçya", "Türkiye").
 * Falls back to the English name from COUNTRY_MAP, then to the raw code.
 */
export function getCountryNameTr(code: string | null | undefined): string {
  if (!code) return "";
  const upper = code.toUpperCase();
  return COUNTRY_MAP_TR[upper] ?? COUNTRY_MAP[upper] ?? upper;
}